import { useEffect, useState } from 'react';
import useWebSocket from '../../api/websocket';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import DurationDisplay, { formatFileSize } from '../common/DurationDisplay';

export default function StreamStatus({ compact = false }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'stream_status') {
      setServers((prev) =>
        prev.map((s) => (s.id === data.payload.id ? { ...s, ...data.payload } : s))
      );
    }
  });

  useEffect(() => {
    const unsub = subscribe('stream_status', (data) => {
      setServers((prev) =>
        prev.map((s) => (s.id === data.payload.id ? { ...s, ...data.payload } : s))
      );
    });

    const load = () => api.get('/api/streaming/status').then(({ data }) => {
      setServers(data);
      setLoading(false);
    }).catch(() => setLoading(false));

    load();
    // Refresco periódico: el endpoint ya trae oyentes en vivo desde Icecast
    const poll = setInterval(load, 15000);

    return () => {
      unsub();
      clearInterval(poll);
    };
  }, [subscribe]);

  if (loading) {
    return (
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-4 shadow-xl">
        <p className="text-sm text-dark-500">Loading stream status...</p>
      </div>
    );
  }

  const primaryServer = servers.find((s) => s.is_default) || servers[0];

  if (compact) {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-dark-850 border border-dark-700 px-4 py-3 shadow-xl">
        <span className="text-xl">{primaryServer?.is_default ? '📡' : '📶'}</span>
        <div>
          <p className="text-sm font-medium text-dark-100">{primaryServer?.name || 'N/A'}</p>
          <p className="text-xs text-dark-500">
            Listeners: {primaryServer?.listener_count || 0}
          </p>
        </div>
        <StatusBadge status={primaryServer?.status || 'disconnected'} size="sm" />
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-dark-850 border border-dark-700 p-4 shadow-xl">
      <h3 className="mb-2 text-base font-semibold text-dark-100">Stream Status</h3>
      <div className="space-y-2">
        {servers.map((server) => (
          <div key={server.id} className="flex items-center justify-between rounded-lg bg-dark-800/50 p-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base">📡</span>
              <div className="min-w-0">
                <p className="font-medium text-sm text-dark-100 truncate">{server.name}</p>
                <p className="text-[10px] text-dark-500 truncate">
                  {server.host}:{server.port}{server.mount}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <p className="text-sm font-medium text-dark-100">{server.listener_count || 0}</p>
                <p className="text-[10px] text-dark-500">listeners</p>
              </div>
              <StatusBadge status={server.status || 'disconnected'} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
